import React, { useState, useEffect } from "react";
import {
	Container,
	Form,
	Button,
	Card,
	Alert,
	InputGroup,
} from "react-bootstrap";
import { Eye, EyeOff } from "lucide-react";
import { useParams, useLocation } from "react-router-dom";
import authService from "../services/authService";

const PasswordResetConfirmComponent = () => {
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("danger");
	const [isValidLink, setIsValidLink] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const { uid, token } = useParams();
	const location = useLocation();
	const [email, setEmail] = useState("");

	const isSetPasswordFlow = location.pathname.includes("/set-password/");

	useEffect(() => {
		const validateLink = async () => {
			const result = await authService.validateResetLink(uid, token);

			setIsValidLink(result.validLink);

			if (result.validLink) {
				setEmail(result.email);
			} else {
				setMessage("Invalid or expired reset link.");
			}
		};

		validateLink();

	}, [uid, token]);

	const handlePasswordReset = async (event) => {
		event.preventDefault();

		if (newPassword !== confirmPassword) {
			setMessage("Passwords do not match.");
			setMessageType("danger");
			return;
		}

		if (newPassword.length < 8) {
			setMessage("Password must be at least 8 characters long.");
			setMessageType("danger");
			return;
		}

		if (/^\d+$/.test(newPassword)) {
			setMessage("Password cannot be entirely numeric.");
			setMessageType("danger");
			return;
		}

		try {
			const result = await authService.confirmPasswordReset(
				uid,
				token,
				newPassword
			);

			setMessage(result.message);
			setMessageType(result.success ? "success" : "error");

			if (result.success) {
				const loginResult = await authService.login(email, newPassword);
				setMessage(loginResult.message);
			}
		} catch (error) {
			setMessage(
				error.message ||
					"An error occurred during password reset. Try again with a different password or contact support."
			);
			setMessageType("danger");
		}
	};

	const toggleNewPasswordVisibility = () => {
		setShowNewPassword(!showNewPassword);
	};

	const toggleConfirmPasswordVisibility = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	if (!isValidLink && message) {
		return (
			<Container
				className="d-flex justify-content-center align-items-center"
				style={{ minHeight: "100vh" }}
			>
				<Alert variant="danger" className="text-center">
					{message}
				</Alert>
			</Container>
		);
	}

	return (
		<Container
			className="d-flex justify-content-center align-items-center"
			style={{ minHeight: "100vh" }}
		>
			<div className="row w-100 justify-content-center">
				<div className="col-12 col-md-6">
					<Card.Body>
						<div className="text-center mb-4">
							<img
								src="/images/plasticise-logo.png"
								alt="Company Logo"
								className="img-fluid mb-4"
								style={{ maxHeight: "150px", maxWidth: "150px" }}
							/>
						</div>

						<h2 className="text-center mb-4">
							{isSetPasswordFlow ? "Set Your Password" : "Reset Your Password"}
						</h2>

						<Form onSubmit={handlePasswordReset}>
							<Form.Group className="mb-3" controlId="formNewPassword">
								<InputGroup>
									<Form.Control
										type={showNewPassword ? "text" : "password"}
										placeholder="New Password"
										value={newPassword}
										onChange={(e) => setNewPassword(e.target.value)}
										required
									/>
									<Button
										variant="outline-secondary"
										onClick={toggleNewPasswordVisibility}
										type="button"
										className="d-flex align-items-center justify-content-center"
										style={{ width: "48px" }}
									>
										{showNewPassword ? (
											<EyeOff size={20} id="hideNewPassword" />
										) : (
											<Eye size={20} id="showNewPassword" />
										)}
									</Button>
								</InputGroup>
								<Form.Text className="text-muted">
									Password must be at least 8 characters long
								</Form.Text>
							</Form.Group>

							<Form.Group className="mb-3" controlId="formConfirmPassword">
								<InputGroup>
									<Form.Control
										type={showConfirmPassword ? "text" : "password"}
										placeholder="Confirm New Password"
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										required
									/>
									<Button
										variant="outline-secondary"
										onClick={toggleConfirmPasswordVisibility}
										type="button"
										className="d-flex align-items-center justify-content-center"
										style={{ width: "48px" }}
									>
										{showConfirmPassword ? (
											<EyeOff size={20} id="hideConfirmPassword" />
										) : (
											<Eye size={20} id="showConfirmPassword" />
										)}
									</Button>
								</InputGroup>
							</Form.Group>

							<Button
								variant="primary"
								type="submit"
								className="w-100"
								disabled={!isValidLink}
							>
								{isSetPasswordFlow ? "Set Password" : "Reset Password"}
							</Button>

							{message && (
								<Alert
									variant={messageType === "success" ? "success" : "danger"}
									className="mt-3 text-center"
								>
									{message}
								</Alert>
							)}
						</Form>
					</Card.Body>
				</div>
			</div>
		</Container>
	);
};

export default PasswordResetConfirmComponent;